import { chain, splice } from 'icepick';
import {
	BACKLOG,
	SPRINT_MOVE_TO_BOTTOM,
	SPRINT_MOVE_TO_TOP,
} from '../../../../model/constants.tsx';
import { SET_APP_DATA, type SetAppDataAction } from '../../../actions/app/index.tsx';
import {
	SPRINT_CREATE_REQUEST,
	SPRINT_CREATE_SUCCESS,
	type SprintCreateRequestAction,
	type SprintCreateSuccessAction,
} from '../../../actions/sprints/create/index.tsx';
import {
	SPRINT_DELETE_REQUEST,
	type SprintDeleteRequestAction,
} from '../../../actions/sprints/delete/index.tsx';
import {
	SPRINT_MOVE_REQUEST,
	type SprintMoveRequestAction,
} from '../../../actions/sprints/move/index.tsx';
import type { CardListOrderEntityState } from './types.tsx';

type Action =
	| SetAppDataAction
	| SprintCreateRequestAction
	| SprintCreateSuccessAction
	| SprintDeleteRequestAction
	| SprintMoveRequestAction;

const defaultState: CardListOrderEntityState = [];

const cardListOrderReducer = (
	state: CardListOrderEntityState = defaultState,
	action: Action,
): CardListOrderEntityState => {
	switch (action.type) {
		case SET_APP_DATA:
			return action.payload.entities.cardListOrder;
		case SPRINT_CREATE_REQUEST: {
			const backlogIndex = state.indexOf(BACKLOG);
			return splice(state, backlogIndex, 0, action.payload.temporaryCardListId);
		}
		case SPRINT_CREATE_SUCCESS: {
			const tempIndex = state.indexOf(action.payload.temporaryCardListId);
			return splice(state, tempIndex, 1, action.payload.sprint.id);
		}
		case SPRINT_DELETE_REQUEST:
			return splice(state, state.indexOf(action.payload.sprintId), 1);

		case SPRINT_MOVE_REQUEST: {
			const {
				payload: { sprintId, targetSprintId, direction },
			} = action;

			const sprintIndex = state.indexOf(sprintId);
			const otherSprintIndex = state.indexOf(targetSprintId);

			if (direction === SPRINT_MOVE_TO_TOP) {
				const activeSprints = state.slice(0, otherSprintIndex);
				const inactiveSprints = state.slice(otherSprintIndex);
				inactiveSprints.pop(); // pop the "BACKLOG" sprint
				const sprintIndexWithinInactives = inactiveSprints.indexOf(sprintId);
				// move sprint to the top of targetSprintId, but below active sprints
				return [
					...activeSprints,
					sprintId,
					...splice(inactiveSprints, sprintIndexWithinInactives, 1),
					BACKLOG,
				];
			}

			if (direction === SPRINT_MOVE_TO_BOTTOM) {
				const sprints = state;
				sprints.pop(); // pop the "BACKLOG" sprint
				const sprintIndexWithinSprints = sprints.indexOf(sprintId);
				return [...splice(sprints, sprintIndexWithinSprints, 1), sprintId, BACKLOG];
			}

			return chain(state).set(sprintIndex, targetSprintId).set(otherSprintIndex, sprintId).value();
		}
		default: {
			const _exhaustiveCheck: never = action;
			return state;
		}
	}
};

export default cardListOrderReducer;
